import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { resolvePaymentMode, DEFAULT_DEPOSIT_NOTICE_TEXT } from '@limo/shared/pricing'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

// Setting keys (shared with the booking flow's server-side readers)
const PAYMENT_SETTINGS = {
  REQUIRE_DEPOSIT: 'booking_require_deposit',
  PAYMENT_MODE: 'booking_payment_mode',
  DEPOSIT_PERCENTAGE: 'booking_deposit_percentage',
  DEPOSIT_MINIMUM_AMOUNT: 'booking_deposit_minimum_amount',
  DEPOSIT_NOTICE_ENABLED: 'booking_deposit_notice_enabled',
  DEPOSIT_NOTICE_TEXT: 'booking_deposit_notice_text',
} as const

// The three ways an account can collect. `full` and `deposit` are what the old
// Require Deposit switch expressed as off/on; `customer_choice` surfaces a
// deposit-or-full toggle on the customer's payment step.
const PAYMENT_MODE_OPTIONS = [
  {
    value: 'full' as const,
    label: 'Always pay in full',
    description: 'Customers pay the whole amount when they book.',
  },
  {
    value: 'deposit' as const,
    label: 'Always pay a deposit',
    description: 'Customers pay a deposit now and the balance at pickup.',
  },
  {
    value: 'customer_choice' as const,
    label: 'Let the customer choose',
    description: 'Customers pick a deposit or paying in full at checkout.',
  },
]

const paymentSchema = z.object({
  payment_mode: z.enum(['full', 'deposit', 'customer_choice']).default('full'),
  deposit_percentage: z.coerce.number().min(0).max(100, 'Must be between 0 and 100').optional(),
  deposit_minimum_amount: z.coerce.number().min(0, 'Must be 0 or greater').optional(),
  deposit_notice_enabled: z.boolean().default(false),
  deposit_notice_text: z.string().max(300, 'Keep the notice under 300 characters').optional(),
}).refine(
  // A deposit of "0% or $0, whichever is higher" is $0 — the customer would
  // book the trip without paying anything. The server refuses this too
  // (`isDepositRuleUsable`), but catching it here means the operator finds out
  // at save time rather than through a free booking.
  (data) =>
    data.payment_mode === 'full' ||
    (data.deposit_percentage ?? 0) > 0 ||
    (data.deposit_minimum_amount ?? 0) > 0,
  {
    message: 'Set a deposit percentage or a minimum amount — otherwise customers pay nothing',
    path: ['deposit_percentage'],
  }
)

type PaymentFormData = z.infer<typeof paymentSchema>

export function PaymentAtCheckoutCard() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Same query key as BookingPage so both share one cache entry
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  const form = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      payment_mode: 'full',
      deposit_percentage: 25,
      deposit_minimum_amount: 0,
      deposit_notice_enabled: false,
      deposit_notice_text: '',
    },
  })

  const paymentMode = form.watch('payment_mode')
  // Both "always deposit" and "customer chooses" need the deposit amount fields.
  const takesDeposit = paymentMode !== 'full'

  useEffect(() => {
    if (settings) {
      form.reset({
        // The mode key is authoritative; accounts configured before it existed
        // are read from the legacy boolean, so their behaviour is unchanged
        // until someone deliberately picks a different option here.
        payment_mode: resolvePaymentMode({
          accountSettings: {
            paymentMode: settings[PAYMENT_SETTINGS.PAYMENT_MODE],
            requireDeposit: settings[PAYMENT_SETTINGS.REQUIRE_DEPOSIT],
          },
        }),
        deposit_percentage: settings[PAYMENT_SETTINGS.DEPOSIT_PERCENTAGE] ? Number(settings[PAYMENT_SETTINGS.DEPOSIT_PERCENTAGE]) : 25,
        deposit_minimum_amount: settings[PAYMENT_SETTINGS.DEPOSIT_MINIMUM_AMOUNT] ? Number(settings[PAYMENT_SETTINGS.DEPOSIT_MINIMUM_AMOUNT]) : 0,
        deposit_notice_enabled: settings[PAYMENT_SETTINGS.DEPOSIT_NOTICE_ENABLED] === 'true',
        deposit_notice_text: settings[PAYMENT_SETTINGS.DEPOSIT_NOTICE_TEXT] || '',
      })
    }
  }, [settings, form])

  const mutation = useMutation({
    mutationFn: async (data: PaymentFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: PAYMENT_SETTINGS.PAYMENT_MODE,
          value: data.payment_mode,
          description: 'How much is collected at checkout: full, deposit, or customer choice',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PAYMENT_SETTINGS.REQUIRE_DEPOSIT,
          // Kept in step with the mode. Several readers still consult this
          // boolean, and writing it here means none of them have to learn about
          // modes for the answer to stay correct.
          value: String(data.payment_mode !== 'full'),
          description: 'Require deposit for bookings (legacy mirror of booking_payment_mode)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PAYMENT_SETTINGS.DEPOSIT_PERCENTAGE,
          value: String(data.deposit_percentage ?? 25),
          description: 'Deposit percentage of total',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PAYMENT_SETTINGS.DEPOSIT_MINIMUM_AMOUNT,
          value: String(data.deposit_minimum_amount ?? 0),
          description: 'Minimum deposit in dollars; the customer pays this or the percentage, whichever is higher',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PAYMENT_SETTINGS.DEPOSIT_NOTICE_ENABLED,
          value: String(data.deposit_notice_enabled),
          description: 'Show a bold notice under the deposit choice at checkout',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PAYMENT_SETTINGS.DEPOSIT_NOTICE_TEXT,
          // Empty falls back to the default copy on the viewer
          value: (data.deposit_notice_text ?? '').trim(),
          description: 'Custom text for the deposit notice at checkout; empty uses the default',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Deposit settings saved',
        description: 'Your payment at checkout settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-24" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment at Checkout</CardTitle>
        <CardDescription>
          How much customers pay when they book
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="payment_mode"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="space-y-2"
                    >
                      {PAYMENT_MODE_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          htmlFor={`payment-mode-${option.value}`}
                          className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent/50 transition-colors has-[:checked]:border-primary"
                        >
                          <RadioGroupItem
                            value={option.value}
                            id={`payment-mode-${option.value}`}
                            className="mt-0.5"
                          />
                          <div className="space-y-0.5">
                            <p className="text-sm font-medium leading-none">{option.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {option.description}
                            </p>
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {takesDeposit && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="deposit_percentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Deposit Percentage</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              placeholder="25"
                              className="w-24"
                              {...field}
                            />
                            <span className="text-muted-foreground">%</span>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Percentage of total booking amount
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deposit_minimum_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Minimum Deposit</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">$</span>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              placeholder="0"
                              className="w-28"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormDescription>
                          Customers pay this or the percentage, whichever is higher. Leave at 0
                          for no minimum.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* The clamp is worth saying out loud: a minimum above the
                    fare is not an error, it just means the customer pays
                    in full and no balance is left to collect. */}
                {(form.watch('deposit_minimum_amount') ?? 0) > 0 && (
                  <p className="text-xs text-muted-foreground rounded-md bg-muted/50 p-3">
                    A deposit is never larger than the trip itself. On a booking cheaper than
                    your ${Number(form.watch('deposit_minimum_amount') ?? 0).toFixed(2)}{' '}
                    minimum, the customer simply pays the full amount.
                  </p>
                )}
              </div>
            )}

            {/* The notice renders inside the deposit-or-full chooser on the
                payment page, which only exists in customer-choice mode — so
                the controls only appear when they can have any effect. */}
            {paymentMode === 'customer_choice' && (
              <div className="rounded-lg border p-4 space-y-4">
                <FormField
                  control={form.control}
                  name="deposit_notice_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Deposit Notice</FormLabel>
                        <FormDescription>
                          Show a bold notice under the deposit choice at checkout — for
                          example, that deposits are non-refundable.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {form.watch('deposit_notice_enabled') && (
                  <FormField
                    control={form.control}
                    name="deposit_notice_text"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notice Text</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={DEFAULT_DEPOSIT_NOTICE_TEXT}
                            className="resize-none"
                            rows={2}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormDescription>
                          Leave empty to use the default: &quot;{DEFAULT_DEPOSIT_NOTICE_TEXT}&quot;
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}

            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
